import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { isAuthenticated, getCurrentUser, getUserName, getUserType, logout } from '../api/api';
import { MILAN_WORKSPACE_ENABLED, getPostAuthPath } from '../config/productFlags';
import './Navbar.css';

function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [loggedIn, setLoggedIn] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userName, setUserName] = useState('');
  const [userType, setUserType] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setLoggedIn(isAuthenticated());
    setUserEmail(getCurrentUser() || '');
    setUserName(getUserName() || '');
    setUserType(getUserType() || 'brand');
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  const getDashboardLink = () => getPostAuthPath(userType);

  const handleLogout = () => {
    logout();
    setLoggedIn(false);
    setUserEmail('');
    setUserName('');
    setShowDropdown(false);
    navigate('/');
  };

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  return (
    <nav className="navbar">
      <div className="navbar-container">
        <Link to="/" className="navbar-logo">
          <img src="/logo.png" alt="Milan AI" className="logo-image" />
          <span className="logo-text">Milan AI</span>
        </Link>

        {/* Hamburger Menu Button */}
        <button 
          className={`hamburger-btn ${mobileMenuOpen ? 'open' : ''}`}
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
        </button>
        
        <ul className={`navbar-links ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          {/* Find Creators — Milan Workspace only */}
          {MILAN_WORKSPACE_ENABLED && loggedIn && userType === 'brand' && (
            <li><Link to="/find-creators" className={`nav-link ${location.pathname === '/find-creators' ? 'active' : ''}`}>Find Creators</Link></li>
          )}
          {/* Performance Hub is a brand-only product, but we keep it publicly
              discoverable for prospective brands. Once a user is logged in as
              a creator, hide the entry point to avoid confusion — they can't
              actually use PH (the page itself also redirects creators away). */}
          {!(loggedIn && userType === 'creator') && (
            <li>
              <Link
                to="/performance"
                className={`nav-link ${location.pathname.startsWith('/performance') ? 'active' : ''}`}
              >
                Performance Hub <span className="nav-live-pill" aria-label="Live feature">LIVE</span>
              </Link>
            </li>
          )}
          <li>
            <Link to="/services" className={`nav-link ${location.pathname === '/services' ? 'active' : ''}`}>
              Services
            </Link>
          </li>
          <li>
            <Link to="/resources" className={`nav-link ${location.pathname === '/resources' ? 'active' : ''}`}>
              Free Tools
            </Link>
          </li>
          <li>
            <Link to="/pricing" className={`nav-link ${location.pathname === '/pricing' ? 'active' : ''}`}>
              Pricing
            </Link>
          </li>
          <li>
            <Link to="/contact" className={`nav-link ${location.pathname === '/contact' ? 'active' : ''}`}>
              Contact
            </Link>
          </li>
          
          {/* Mobile-only auth links */}
          <li className="mobile-auth-links">
            {loggedIn ? (
              <>
                <Link to={getDashboardLink()} className="nav-link">Dashboard</Link>
                <button className="nav-link logout-link" onClick={handleLogout}>Sign Out</button>
              </>
            ) : (
              <>
                <Link to="/login" className="nav-link">Login</Link>
                <Link to="/signup" className="nav-link signup-link">Sign Up</Link>
              </>
            )}
          </li>
        </ul>
        
        <div className="navbar-auth">
          {loggedIn ? (
            <div className="user-menu" ref={dropdownRef}>
              <button 
                className="user-button"
                onClick={() => setShowDropdown(!showDropdown)}
              >
                <span className="user-avatar">
                  {(userName || userEmail).charAt(0).toUpperCase()}
                </span>
                <span className="user-name">{userName || userEmail}</span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="m6 9 6 6 6-6"/>
                </svg>
              </button>
              {showDropdown && (
                <div className="dropdown-menu">
                  {userType === 'brand' && MILAN_WORKSPACE_ENABLED && (
                    <Link
                      to="/brand-dashboard"
                      className="dropdown-item dropdown-item-workspace"
                      onClick={() => setShowDropdown(false)}
                    >
                      Milan Workspace
                    </Link>
                  )}
                  {userType === 'brand' && (
                    <Link
                      to="/performance"
                      className="dropdown-item dropdown-item-performance"
                      onClick={() => setShowDropdown(false)}
                    >
                      {MILAN_WORKSPACE_ENABLED ? 'Performance Hub' : 'Dashboard'}
                    </Link>
                  )}
                  {userType === 'creator' && (
                    <Link
                      to="/creator-dashboard"
                      className="dropdown-item"
                      onClick={() => setShowDropdown(false)}
                    >
                      Dashboard
                    </Link>
                  )}
                  <hr className="dropdown-divider" />
                  <button className="dropdown-item logout" onClick={handleLogout}>
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              <Link to="/login" className="btn-login">Login</Link>
              <Link to="/signup" className="btn-signup">Sign Up</Link>
            </>
          )}
        </div>
      </div>

      {/* Mobile menu overlay */}
      {mobileMenuOpen && <div className="mobile-overlay" onClick={() => setMobileMenuOpen(false)}></div>}
    </nav>
  );
}

export default Navbar;
